/**
 * PATH: apps/quote/src/components/builder/builder-workspace/BuilderWorkspaceLayout.tsx
 * PURPOSE: 3-pane layout component with drag-and-drop file support
 */

import React, { useState, useCallback, useRef, type ReactNode } from 'react'
import { useWorkspace } from './BuilderWorkspaceProvider'

interface BuilderWorkspaceLayoutProps {
  leftPanel: ReactNode
  centerContent: ReactNode
  rightDrawer: ReactNode
  rightRail?: ReactNode
  bottomBar: ReactNode
  header?: ReactNode
  onFileDrop?: (files: File[]) => void
}

export function BuilderWorkspaceLayout({
  leftPanel, centerContent, rightDrawer, rightRail, bottomBar, header, onFileDrop,
}: BuilderWorkspaceLayoutProps) {
  const { leftPanelOpen, leftPanelWidth, rightDrawerOpen, rightDrawerWidth, openRightDrawer } = useWorkspace()
  const [isDragging, setIsDragging] = useState(false)
  const dragCounter = useRef(0)

  const handleDragEnter = useCallback((e: React.DragEvent) => {
    e.preventDefault(); e.stopPropagation(); dragCounter.current++
    if (e.dataTransfer.types.includes('Files')) setIsDragging(true)
  }, [])

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault(); e.stopPropagation(); dragCounter.current--
    if (dragCounter.current === 0) setIsDragging(false)
  }, [])

  const handleDragOver = useCallback((e: React.DragEvent) => { e.preventDefault(); e.stopPropagation() }, [])

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault(); e.stopPropagation(); setIsDragging(false); dragCounter.current = 0
    if (e.dataTransfer.files?.length) {
      const files = Array.from(e.dataTransfer.files)
      if (onFileDrop) onFileDrop(files)
      else openRightDrawer('context')
    }
  }, [onFileDrop, openRightDrawer])

  return (
    <div className="h-full min-h-0 max-h-full flex flex-col bg-slate-50 dark:bg-slate-950 overflow-hidden relative"
      onDragEnter={handleDragEnter} onDragLeave={handleDragLeave} onDragOver={handleDragOver} onDrop={handleDrop}
    >
      {isDragging && (
        <div className="absolute inset-0 z-50 bg-blue-500/10 backdrop-blur-sm flex items-center justify-center border-4 border-dashed border-blue-400 rounded-lg m-2 pointer-events-none">
          <div className="text-center">
            <svg className="w-16 h-16 text-blue-500 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
            </svg>
            <p className="text-xl font-semibold text-blue-700 dark:text-blue-300">Drop files here</p>
            <p className="text-sm text-blue-600 dark:text-blue-400">PDFs, images, Excel files</p>
          </div>
        </div>
      )}

      {header && <div className="flex-shrink-0 z-30">{header}</div>}

      <div className="flex-1 min-h-0 flex overflow-hidden relative">
        <div className="flex-shrink-0 flex flex-col min-h-0 bg-white dark:bg-slate-900 border-r border-slate-200 dark:border-slate-800 overflow-hidden transition-all duration-200 ease-out"
          style={{ width: leftPanelOpen ? leftPanelWidth : 0, opacity: leftPanelOpen ? 1 : 0 }}>
          {leftPanelOpen && leftPanel}
        </div>
        <div className="flex-1 min-w-0 min-h-0 flex flex-col overflow-hidden">
          <div className="flex-1 min-h-0 overflow-y-auto overflow-x-auto">{centerContent}</div>
        </div>
        <div className="flex-shrink-0 flex flex-col min-h-0 bg-white dark:bg-slate-900 border-l border-slate-200 dark:border-slate-800 overflow-hidden transition-all duration-200 ease-out"
          style={{ width: rightDrawerOpen ? rightDrawerWidth : 0, opacity: rightDrawerOpen ? 1 : 0 }}>
          {rightDrawerOpen && rightDrawer}
        </div>
        {rightRail}
      </div>

      <div className="flex-shrink-0 z-20 pb-safe">{bottomBar}</div>
    </div>
  )
}
